import { useState } from "react";
import { Box, FormDialog, Flex, FormControl, Typography, MenuItem, Select, TextField } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import { UserAccountType } from "../../types/user";
import useMutateUser from "../../hooks/use-mutate-user";
import { FormControlLabel, Checkbox, FormGroup, Divider } from "@mui/material";
import { Role } from "@adora/shared/types/auth";
import { z } from "zod";
import usePublishers from "../../hooks/use-publishers";

interface Brand {
  id: number;
  name: string;
}

interface CreateUserDialogProps extends BaseDialogProps {
  brandId?: string;
  type: UserAccountType;
  brands: Brand[];
}

type FormRole = Role | "admin" | "media";

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ type, open, onClose, brands }) => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [role, setRole] = useState<FormRole>("customer");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { create: createUser } = useMutateUser();
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [brandError, setBrandError] = useState<string>("");
  const [publisherId, setPublisherId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [publisherError, setPublisherError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const { publishers } = usePublishers();

  const shouldDisplayEmail = role !== "admin";

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setBrandError("");
    setPublisherError("");
    setEmailError("");

    const schema = z.object({
      ...(role === "media" && {
        publisherId: z.string().min(1, "Please select a publisher")
      }),
      ...(role !== "admin" &&
        role !== "media" && {
          brands: z.array(z.string()).min(1, "Please select at least one brand")
        }),
      ...(shouldDisplayEmail && {
        email: z.email("Please enter a valid email address")
      })
    });

    const result = schema.safeParse({
      ...(role === "media" && { publisherId }),
      ...(role !== "admin" && role !== "media" && { brands: selectedBrands }),
      ...(shouldDisplayEmail && { email: email.trim() })
    });

    if (!result.success) {
      for (const issue of result.error.issues) {
        if (issue.path.includes("publisherId")) setPublisherError(issue.message);
        if (issue.path.includes("brands")) setBrandError(issue.message);
        if (issue.path.includes("email")) setEmailError(issue.message);
      }
      setSubmitting(false);
      return;
    }

    type RoleMapping = {
      role: Role | null;
      accountType: UserAccountType;
    };

    const roleAccountType: { [key: string]: RoleMapping } = {
      admin: {
        role: "internal",
        accountType: "internal"
      },
      internal: {
        role: "internal",
        accountType: "app"
      },
      customer: {
        role: "customer",
        accountType: "app"
      },
      media: {
        role: "customer",
        accountType: "media"
      }
    };

    const { role: userRole, accountType: userAccountType } = roleAccountType[role || "customer"];

    await createUser({
      name: username,
      password,
      brands: role === "media" ? undefined : selectedBrands,
      role: userRole || "customer",
      accountType: userAccountType,
      ...(shouldDisplayEmail && {
        email: email.trim()
      }),
      ...(role === "media" && {
        rmnPublisherId: Number(publisherId)
      })
    });
    setSubmitting(false);
    onClose(true);
  };

  const handleBrandChange = (brandId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedBrands((prev) => (event.target.checked ? [...prev, brandId] : prev.filter((id) => id !== brandId)));
  };

  const allBrandIds = brands.map((b) => b.id.toString());
  const allBrandsSelected = allBrandIds.length > 0 && allBrandIds.every((id) => selectedBrands.includes(id));
  const someBrandsSelected = allBrandIds.some((id) => selectedBrands.includes(id));

  const handleSelectAllBrands = () => {
    setSelectedBrands(allBrandsSelected ? [] : allBrandIds);
  };

  const description = type === "app" ? "Create a new internal user" : "Create a new user for a brand";

  return (
    <FormDialog
      title="Create User"
      description={description}
      open={open}
      onClose={onClose}
      onSubmit={handleCreate}
      primaryButtonText="Create"
      isPrimaryButtonDisabled={submitting}
      dataDogIdentifier="create user"
    >
      <Flex flexDirection="column">
        <Box pb="8px">
          <Typography variant="body1">User Name</Typography>
        </Box>
        <TextField
          type="text"
          name="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username"
          required
          fullWidth
          dataDogIdentifier="create user username"
        />
        <Box pb="8px" pt="24px">
          <Typography variant="body1">Password</Typography>
        </Box>
        <TextField
          type="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          size="medium"
          required
          fullWidth
          dataDogIdentifier="create user password"
        />
        <>
          <Box pb="8px" pt="24px">
            <Typography variant="body1">Account Type</Typography>
          </Box>
          <Select
            type="text"
            name="role"
            value={role}
            onChange={(e) => {
              const newRole = e.target.value as FormRole;
              setRole(newRole);
              if (newRole === "admin") {
                setEmail("");
                setEmailError("");
              }
              if (newRole !== "media") {
                setPublisherId("");
                setPublisherError("");
              }
            }}
            size="medium"
            required
          >
            <MenuItem size="medium" value="internal">
              Internal
            </MenuItem>
            <MenuItem size="medium" value="customer">
              Customer
            </MenuItem>
            <MenuItem size="medium" value="media">
              Media
            </MenuItem>
            <MenuItem size="medium" value="admin">
              Admin UI{" "}
              <Typography pl="10px" variant="caption">
                (Access to admin ui only)
              </Typography>
            </MenuItem>
          </Select>
        </>

        {role === "media" && (
          <>
            <Box pb="8px" pt="24px">
              <Typography variant="body1">Publisher ID</Typography>
            </Box>
            <Select
              type="text"
              name="publisherId"
              value={publisherId}
              onChange={(e) => setPublisherId(e.target.value as string)}
              size="medium"
              required
              error={!!publisherError}
            >
              {publishers?.map((publisher) => (
                <MenuItem key={publisher.id} size="medium" value={publisher.id.toString()}>
                  {publisher.name}
                </MenuItem>
              ))}
            </Select>
            {publisherError && (
              <Typography color="error" variant="caption" pt="8px">
                {publisherError}
              </Typography>
            )}
          </>
        )}

        {shouldDisplayEmail && (
          <>
            <Box pb="8px" pt="24px">
              <Typography variant="body1">Email</Typography>
            </Box>
            <TextField
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              size="medium"
              required
              fullWidth
              error={!!emailError}
              helperText={emailError}
              dataDogIdentifier="create user email"
            />
          </>
        )}

        {role !== "admin" && role !== "media" && (
          <>
            <Typography variant="body1" pt="24px" pb="8px">
              Brands
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "12px",
                alignSelf: "stretch",
                borderRadius: "8px",
                border: brandError ? "1px solid red" : "1px solid rgba(0, 0, 0, 0.23)",
                background: "rgba(168, 171, 185, 0.05)",
                padding: "12px 16px"
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allBrandsSelected}
                    indeterminate={someBrandsSelected && !allBrandsSelected}
                    onChange={handleSelectAllBrands}
                  />
                }
                label="Select all"
              />
              <Divider sx={{ alignSelf: "stretch" }} />
              <FormControl>
                <FormGroup>
                  {brands.map((brand) => (
                    <FormControlLabel
                      key={brand.id}
                      control={
                        <Checkbox
                          checked={selectedBrands.includes(brand.id.toString())}
                          onChange={handleBrandChange(brand.id.toString())}
                        />
                      }
                      label={brand.name}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
            {brandError && (
              <Typography color="error" variant="caption" pt="8px">
                {brandError}
              </Typography>
            )}
          </>
        )}
      </Flex>
    </FormDialog>
  );
};

export default CreateUserDialog;
